import { Accordion, AccordionDetails, AccordionSummary, Box, Link, useTheme } from "@mui/material";
import { format } from "date-fns";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IDateRange } from "../../common/models/IDateRange";
import { ITagModel } from "../../common/models/ITagModel";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useTagValuesCharts } from "../../queries/tag-values-charts/UseTagValuesCharts";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { ITagViewState } from "../../services/state-data-service/Models";
import { ITagHistoryItem } from "../../services/tag-data-service/Models";
import NoData from "../no-data/NoData";
import TagValueChart from "../tag-history/TagValuesChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ObjectTagValuesUtils from "./ObjectTagValuesUtils";
import { getTagRoute } from "../../common/utilities/RouteHelpers";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { ITagCategory } from "../../common/models/ITagCategory";
import { IEntityValueDeviceModel, IEntityValueTankModel, IEntityValueWellModel } from "../../common/models/IEntityValueModels";
import { ITankBattery } from "../../common/models/ITankBattery";
import { IListTextTagMapping_V2 } from "../../common/models/ILookupList";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";

const DATE_FORMAT_STRING = "M/dd/yyyy";
const TIME_FORMAT_STRING = "h:mm:ss a";

export type TagValueChartListItem = {
    historyItem: ITagHistoryItem | undefined;
    listTextTagMapping?: IListTextTagMapping_V2 | undefined;
    tagDetailInfo: ITagViewState | undefined;
}

export type TagValueChartGroup = {
    name: string;
    order: number;
    items: Array<TagValueChartListItem>;
}

export type ObjectTagValuesChartListProps = {
    valuesDataResponse: IEntityValueDeviceModel | IEntityValueWellModel | IEntityValueTankModel | ITankBattery | undefined;
    dateRange: IDateRange;
    showAlarmSetpointThresholds: boolean;
    showTagDescriptions: boolean;
    defaultConfigurationTATs: number[];
    categories: ITagCategory[];
    minY?: number;
    maxY?: number;

}

export default function ObjectTagValuesChartList({
    valuesDataResponse,
    dateRange,
    showAlarmSetpointThresholds,
    showTagDescriptions,
    defaultConfigurationTATs,
    categories,
    minY,
    maxY
}: ObjectTagValuesChartListProps) {
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const navigateTo = useNavigateTo();

    const tagAndTatIDs = ObjectTagValuesUtils.getTagAndTatIDs(valuesDataResponse);

    const { data: dataResponse } = useQueryNotifications(useTagValuesCharts)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: routeContext.contextObject?.id as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        tagAndTatIDs: tagAndTatIDs,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        offline: offlineState.offline,
        enabled: true
    });

    const groups = useMemo(() =>
    {
        return ObjectTagValuesUtils.createTagChartGroups(valuesDataResponse, dataResponse?.tagHistoryItems, dataResponse?.listTextTagMapping, 
            categories.filter(o => o.selected), defaultConfigurationTATs, offlineState.offline);
    }, [
        valuesDataResponse,
        dataResponse,
        offlineState.offline,
        defaultConfigurationTATs,
        categories
    ]);

    const renderItem = useCallback((index: number, item: TagValueChartListItem) => {
        const tag = item.tagDetailInfo?.contextObject as ITagModel;
        const date = tag && tag.tagValue && tag.tagValue.timestamp ? new Date(tag.tagValue.timestamp) : undefined;
        const lkvDate = date ? format(date, DATE_FORMAT_STRING) : "";
        const lkvTime = date ? format(date, TIME_FORMAT_STRING) : "";
        const hasValues = (item.historyItem?.values?.length ?? 0) > 0;

        const tagUrl = getTagRoute(UIViewEnum.Tag_Details,
            routeContext.groupID,
            tag.tagObject?.objectType === eLynxObjectTypeEnum.Well ? tag.tagObject?.id : undefined,
            tag.tagObject?.objectType === eLynxObjectTypeEnum.Device ? tag.tagObject?.id : undefined,
            tag.tagObject?.objectType === eLynxObjectTypeEnum.TankBattery ? tag.tagObject?.id : undefined,
            tag.tagObject?.objectType === eLynxObjectTypeEnum.Tank ? tag.tagObject?.id : undefined,
            tag.id);

        const onNavigate = (url: string, shiftKey: boolean, ctrlKey: boolean) => {
            let options: NavigateToOptions | undefined;

            if (shiftKey || ctrlKey) {
                options = {
                    newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                }
            }
    
            
            navigateTo(url, options);
        }

        return (
            <Box key={index}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '.5em',
                    //backgroundColor: 'rgb(232, 232, 232)',
                    border: '1px solid lightgray',
                    marginBottom: '.5em',
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '1em',
                        alignItems: 'center',
                        minWidth: '220px',
                        fontSize: 'larger'
                    }}>
                    {hasValues &&
                        <>
                            <Box>
                                {tag?.tagValue?.displayText}
                            </Box>
                            <Box sx={{
                                fontWeight: 'bold',
                                marginBottom: theme.spacing(2)
                            }}>
                                {item.tagDetailInfo?.tagUnits}
                            </Box>
                            <Box>
                                {lkvDate}
                            </Box>
                            <Box>
                                {lkvTime}
                            </Box>
                        </>
                    }
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        borderLeft: '2px solid rgba(0, 0, 0, 0.5)',
                        flexGrow: 1,
                        marginRight: '1em',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{
                        paddingLeft: '.5em',
                        fontSize: 'larger'
                    }}>
                        <Link
                            sx={{
                                color: "text.primary",
                                "&:hover": {
                                    cursor: "pointer"
                                },
                                fontSize: "larger"
                            }}
                            onClick={(ev) => onNavigate(tagUrl, ev.shiftKey, ev.ctrlKey)}>
                            {item.tagDetailInfo?.contextObject?.displayText}
                        </Link>
                    </Box>
                    {showTagDescriptions &&
                        <Box sx={{
                            paddingLeft: '.5em',
                            fontSize: 'larger',
                            fontStyle: 'italic',
                            color: 'green'
                        }}>
                            {tag.tatDescription}
                        </Box>
                    }
                    <Box 
                        sx={{
                            paddingTop: '1em', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            minHeight: 400,
                            flexBasis: 'auto',
                             flexGrow: 1}}>
                        <TagValueChart
                            listTextTagMapping={item.listTextTagMapping}
                            tagDetailInfo={item.tagDetailInfo}
                            tagHistoryItem={item.historyItem}
                            showThreshold={showAlarmSetpointThresholds}
                            dateRange={dateRange}
                            noTitle
                            noSubtitle
                            noYAxisTitle
                            minY={minY}
                            maxY={maxY}

                        />
                    </Box>
                </Box>
            </Box>
        );
    }, [
        dateRange,
        showAlarmSetpointThresholds,
        showTagDescriptions,
        routeContext.groupID,
        theme,
        navigateTo,
        minY,
        maxY
    ]);

    const renderGroup = useCallback((index: number, group: TagValueChartGroup) => {
        return (
            <Accordion key={index} defaultExpanded disableGutters square>
                <AccordionSummary
                    expandIcon={
                        <ExpandMoreIcon/>}
                    className={"ag-theme-alpine ag-row-group"}
                    sx={{
                        border: '1px solid lightgray',
                        paddingLeft: '1em',
                        flexDirection: 'row-reverse',
                        "& .MuiAccordionSummary-expandIconWrapper": {
                            transform: 'rotate(-90deg)'
                        },
                        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                            transform: 'rotate(0deg)'
                        },
                        alignItems: 'center'
                    }}>
                    {group.name}
                </AccordionSummary>
                <AccordionDetails sx={{padding: '0'}}>
                    {group.items.map((o,i) => renderItem(i,o))}
                </AccordionDetails>
            </Accordion>
        );
    }, [
        renderItem,
    ]);

    return (
        <>
            {groups && groups.length > 0 &&
                <Box sx={{
                    maxHeight: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden'}}>
                    {groups.map((g, i) => renderGroup(i, g))}
                </Box>
            }
            {(!groups || groups.length === 0) &&
                <NoData text={t('objectTagValues.noDataCharts')}></NoData>
            }
        </>
    )
}